import { Store, Briefcase, Wrench, Truck, Bike, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead, BadgeSoon, BtnOrange } from "@/components/bits";
import { usePreRegister } from "@/components/PreRegisterModal";
import { IMAGES } from "@/data/site";

const AREAS = [
  {
    icon: Store,
    title: "Comércio e Descontos",
    desc: "Descubra estabelecimentos, encontre ofertas e aproveite benefícios próximos de você.",
    to: "/empresarios",
    img: IMAGES.skyline,
  },
  {
    icon: Briefcase,
    title: "Empresários",
    desc: "Divulgue seu negócio, apresente ofertas e tenha acesso a recursos de conexão com consumidores.",
    to: "/empresarios",
    img: IMAGES.safetyTech,
  },
  {
    icon: Wrench,
    title: "Serviços",
    desc: "Encontre profissionais, soluções e serviços disponíveis em sua região.",
    to: "/futuro-off360",
    img: IMAGES.passengerRide,
  },
  {
    icon: Truck,
    title: "Entregas",
    desc: "Novas soluções para tornar entregas e deslocamentos de produtos mais práticos.",
    to: "/entregas",
    img: IMAGES.heroUrbanNight,
  },
  {
    icon: Bike,
    title: "360Moto",
    desc: "Uma alternativa futura de mobilidade sobre duas rodas.",
    to: "/360moto",
    img: IMAGES.driverNav,
  },
  {
    icon: Sparkles,
    title: "Oportunidades",
    desc: "Novos recursos para conectar pessoas a oportunidades próximas.",
    to: "/futuro-off360",
    img: IMAGES.safetyTech,
  },
];

export default function Futuro() {
  const { openPreRegister } = usePreRegister();

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Futuro OFF360"
        title="O futuro está mais perto de você."
        lead="O OFF360 está sendo desenvolvido para reunir mobilidade, comércio, benefícios, serviços e novas oportunidades em um mesmo ecossistema. Começamos pelo 360Taxi e estamos preparando novas soluções para os próximos lançamentos."
      />

      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-24 lg:px-8" data-testid="futuro-areas">
        <Reveal>
          <SectionHead eyebrow="Áreas em desenvolvimento" title="Seis frentes para os próximos lançamentos." />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {AREAS.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.06} className="h-full">
              <article
                data-testid={`futuro-card-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={a.img}
                    alt={a.title}
                    loading="lazy"
                    className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4">
                    <BadgeSoon />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <a.icon className="h-7 w-7 text-brand-orange" />
                  <h3 className="mt-4 font-display text-xl font-bold text-slate-900">{a.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{a.desc}</p>
                  {a.to !== "/futuro-off360" ? (
                    <Link
                      to={a.to}
                      data-testid={`futuro-link-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-orange transition-transform duration-300 group-hover:translate-x-1"
                    >
                      Saiba mais <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-400">
                      Em breve neste espaço
                    </span>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-center gap-6 rounded-3xl bg-brand-obsidian px-6 py-14 text-center sm:px-12">
            <h2 className="max-w-xl font-display text-2xl font-extrabold text-white sm:text-3xl">
              Quer ser avisado sobre os próximos lançamentos?
            </h2>
            <p className="max-w-lg text-sm leading-relaxed text-slate-300">
              Registre seu interesse na modalidade desejada e receba informações quando ela estiver disponível.
            </p>
            <BtnOrange
              data-testid="futuro-cta-pre-cadastro"
              onClick={() => openPreRegister({ origem: "futuro-off360" })}
            >
              Quero ser avisado
            </BtnOrange>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
